import { jsxs, jsx } from "react/jsx-runtime";
import { forwardRef, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Icon } from "../icon/icon.es.js";
import { PillWrapper, PillButton, PillText } from "./pill.styles.es.js";
const propTypes = {
  adjoin: PropTypes.oneOf(["left", "none", "right"]),
  appearance: PropTypes.oneOf(["active", "danger", "error", "warning"]),
  autoFocus: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.oneOf(["cancel", "click"])
  ]),
  cancellable: PropTypes.bool,
  children: PropTypes.node.isRequired,
  clickable: PropTypes.bool,
  colorScheme: PropTypes.oneOf(["blue", "green", "red", "yellow"]),
  magnitude: PropTypes.oneOf(["small", "medium"]),
  onCancel: PropTypes.func,
  onClick: PropTypes.func
};
const defaultProps = {
  adjoin: "none",
  appearance: void 0,
  autoFocus: false,
  cancellable: false,
  clickable: false,
  colorScheme: void 0,
  magnitude: "small",
  onCancel: void 0,
  onClick: void 0
};
const Pill = forwardRef(
  ({
    adjoin,
    appearance,
    autoFocus,
    cancellable,
    children,
    clickable,
    colorScheme: colorSchemeProp,
    magnitude,
    onCancel,
    onClick,
    ...restProps
  }, ref) => {
    const clickButtonRef = useRef();
    const cancelButtonRef = useRef();
    const colorScheme = colorSchemeProp ?? {
      active: "green",
      danger: "red",
      error: "red",
      warning: "yellow"
    }[appearance];
    useEffect(() => {
      if (clickable && (autoFocus === true || autoFocus === "click")) {
        if (clickButtonRef.current) {
          clickButtonRef.current.focus();
        }
      } else if (cancellable && (autoFocus === true || autoFocus === "cancel")) {
        if (cancelButtonRef.current) {
          cancelButtonRef.current.focus();
        }
      }
    }, [autoFocus, clickable, cancellable]);
    return /* @__PURE__ */ jsxs(
      PillWrapper,
      {
        ...restProps,
        ref,
        "aria-invalid": appearance === "error",
        children: [
          /* @__PURE__ */ jsx(
            PillButton,
            {
              ref: clickButtonRef,
              as: clickable ? "button" : void 0,
              "data-testid": "pill-click-button",
              onClick: clickable ? onClick : void 0,
              $adjoin: cancellable ? "left" : adjoin,
              $appearance: appearance,
              $clickable: clickable,
              $colorScheme: colorScheme,
              $magnitude: magnitude,
              children: /* @__PURE__ */ jsx(PillText, { children })
            }
          ),
          cancellable && /* @__PURE__ */ jsx(
            PillButton,
            {
              ref: cancelButtonRef,
              "aria-label": "Cancel",
              as: "button",
              "data-testid": "pill-cancel-button",
              onClick: onCancel,
              $adjoin: "right",
              $appearance: appearance,
              $clickable: true,
              $colorScheme: colorScheme,
              $magnitude: magnitude,
              $square: true,
              children: /* @__PURE__ */ jsx(Icon, { type: "close" })
            }
          )
        ]
      }
    );
  }
);
Pill.propTypes = propTypes;
Pill.defaultProps = defaultProps;
export {
  Pill
};
